import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// The jurisdiction → tax-name table and its fallback chain live in
// @releve/shared/documents/taxIdentity, where the server-side document
// assemblers can reach them. Re-exported below so existing importers of
// TAX_NAME_BY_JURISDICTION from this module keep working.
import { TAX_NAME_BY_JURISDICTION, resolveTaxJurisdictionFrom, resolveTaxName } from '@releve/shared/documents/taxIdentity';
import { findEffectiveDefault, isRateEffectiveOn, isSalesTaxJurisdiction } from '@releve/shared';
import { formatLocalISODate } from '@/lib/dateUtils';
import type { TaxRateType } from '@/lib/taxCalculations';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  useOrganizationTaxRates,
  useFullTaxSettings,
  type TaxRateWithLineage,
} from '@/hooks/useOrganizationTaxSettings';

export { TAX_NAME_BY_JURISDICTION, resolveTaxName };

// EU countries with predefined VAT rates (dropdown selection)
const EU_VAT_COUNTRIES = [
  'NL', 'BE', 'FR', 'DE', 'AT', 'LU', 'IT', 'ES', 'PT', 'IE',
  'FI', 'SE', 'DK', 'PL', 'CZ', 'HU', 'SK', 'SI', 'EE', 'LV',
  'LT', 'RO', 'BG', 'HR', 'CY', 'MT', 'GR'
] as const;
type EUCountry = typeof EU_VAT_COUNTRIES[number];

export type TaxDirection = 'INPUT' | 'OUTPUT';

export interface TaxRate {
  key: string;
  value: number | string; // numeric rate (0.21) or special value ('rc', 'lookup', -1)
  label: string;
  numericValue: number; // For calculation: the actual percentage (21, 9, etc.) or 0 for special values
  requiresExemptionReason: boolean;
  /**
   * A retired DB rate (`is_active = false`) that is still referenced by a line
   * on the document being edited. Listed so the line keeps displaying its
   * stored rate; never a default and not selectable for other lines. Also set
   * on the synthetic "Exempt" of a VAT purchase picker, listed only for a
   * legacy exempt line (`hasLegacyExempt`).
   */
  isInactive?: boolean;
  /**
   * The rate is a row of the `tax_rates` table (`key` is its id), as opposed to
   * a synthetic treatment ('exempt', 'no_tax', 'no_vat', 'non_reclaimable'). The one source of
   * truth for "does this option carry a `tax_rate_id`" — read it through
   * {@link taxRateIdOf}, never by inspecting the key's shape.
   */
  fromDb?: true;
  /**
   * An active DB rate whose window does NOT cover the document's day (retired
   * by a rate change, or not started yet) — `{ from, to }` is its window. It
   * is listed after the in-window rates, under its own heading, and may be
   * picked deliberately (a credit note or a late supplier bill for an earlier
   * supply, an advance invoice): VAT follows the date of the supply, not of
   * the document. It is never a default, never a scan or percentage match,
   * and the line shows the "not in force" warning (docs/TAX.md).
   */
  otherDate?: { from: string | null; to: string | null };
}

/**
 * The `tax_rate_id` a line takes when this rate is picked: the DB row id for a
 * `tax_rates` row, `undefined` for a synthetic treatment (or no rate).
 */
export function taxRateIdOf(rate?: TaxRate | null): string | undefined {
  return rate?.fromDb ? rate.key : undefined;
}

export interface ExemptionReason {
  key: string;
  label: string;
  invoiceText?: string;
  placeholder?: string;
  requiresCertificate?: boolean;
}

export interface JurisdictionTaxConfig {
  // Jurisdiction info
  jurisdiction: string | null;
  jurisdictionName: string;

  // Tax system
  taxSystem: 'VAT' | 'SALES_TAX';
  taxName: string; // "VAT", "BTW", "TVA", "Sales Tax"

  // Rates
  rates: TaxRate[];
  defaultRate: string;

  // Exemption reasons
  exemptionReasons: ExemptionReason[];
  exemptionRegion: 'eu' | 'us';

  // UI labels
  taxColumnLabel: string;
  totalTaxLabel: string;

  // Filing rhythm — days between period_end and the legal due date.
  // Drives the "Due {date}" label and overdue detection on /accounting/tax.
  filingLagDays: number;

  // How this jurisdiction states tax amounts on the VAT return. Display and
  // pre-fill only — the authoritative copy is vat_filing_rounding_policy() in
  // the database, consulted by file_vat_return_safe.
  filingRounding: FilingRoundingPolicy;

  // organization_tax_settings.vat_filing_start_date ('YYYY-MM-DD') or null.
  // The first day of the first period this org files from Relevé: earlier
  // periods cannot be filed and earlier entries never enter a return. Display
  // and client-side validation only — file_vat_return_safe enforces it.
  filingStartDate: string | null;

  // Status
  isConfigured: boolean;
  error: 'country_required' | null;

  /**
   * The day the picker's rates are filtered on (`asOfDate`, else today; null
   * on the direction-less tax pages). Optional so test fixtures may omit it.
   */
  asOfDay?: string | null;
  /**
   * Every org rate row, unfiltered (expired and future-dated included), with
   * its lineage ids — what `rateInForce` walks to move an account default to
   * the rate in force on the document date, and what the date re-validation
   * reads windows from. Optional so test fixtures may omit it.
   */
  rateRows?: readonly TaxRateWithLineage[];
}

/**
 * 'cents' = the return is filed at exact ledger amounts (default).
 * 'whole' = the return is filed in whole currency units; `mode` picks the
 * default rounding: 'favor' rounds in the filer's favor (output down, input
 * up — NL), 'nearest' rounds arithmetically (reserved, e.g. FR).
 */
export type FilingRoundingPolicy =
  | { precision: 'cents' }
  | { precision: 'whole'; mode: 'favor' | 'nearest' };

// Days from period_end to the legal due date by jurisdiction. Sourced from
// each tax authority's published filing schedule. Used for overdue detection;
// not a hard rule — orgs may have extensions or use accountant deadlines.
const FILING_LAG_DAYS_BY_JURISDICTION: Record<string, number> = {
  NL: 30, // Belastingdienst — last day of month after period
  BE: 20, // FPS Finance — 20th of month after period
  DE: 10, // Finanzamt — 10th of month after period (excl. permanent extension)
  FR: 30, // DGFiP — varies; 30 days is a safe default
  GB: 37, // HMRC — 1 month + 7 days (MTD VAT)
  IE: 23, // Revenue — 23rd of month after period
  ES: 30, // Agencia Tributaria
  IT: 30, // Agenzia delle Entrate
  AT: 45, // Finanzamt — 15th of second month after period
};
const DEFAULT_FILING_LAG_DAYS = 30;

export function getFilingLagDays(jurisdiction: string | null): number {
  if (!jurisdiction) return DEFAULT_FILING_LAG_DAYS;
  if (isSalesTaxJurisdiction(jurisdiction)) return DEFAULT_FILING_LAG_DAYS; // varies per state
  return FILING_LAG_DAYS_BY_JURISDICTION[jurisdiction] ?? DEFAULT_FILING_LAG_DAYS;
}

// MUST exactly mirror the arms of vat_filing_rounding_policy() in the DB
// (20260806090100) — the RPC rejects filed amounts for 'cents' jurisdictions,
// so a client-side entry the server doesn't have would make filing fail hard.
// Germany et al. file tax amounts in cents (their whole-euro rule covers the
// bases only) and deliberately have no entry here.
const FILING_ROUNDING_BY_JURISDICTION: Record<string, FilingRoundingPolicy> = {
  NL: { precision: 'whole', mode: 'favor' }, // Belastingdienst: whole euros, in the filer's favor
};
const DEFAULT_FILING_ROUNDING: FilingRoundingPolicy = { precision: 'cents' };

export function getFilingRoundingPolicy(jurisdiction: string | null): FilingRoundingPolicy {
  if (!jurisdiction) return DEFAULT_FILING_ROUNDING;
  return FILING_ROUNDING_BY_JURISDICTION[jurisdiction] ?? DEFAULT_FILING_ROUNDING;
}

/**
 * Derive the jurisdiction name from a country code using the browser's Intl API.
 * Falls back to the raw code if Intl is unavailable.
 */
export function getJurisdictionName(code: string, locale?: string): string {
  // Handle US state codes like "US-ID" → "ID, United States" (a bare "US"
  // falls through to the country name below).
  const state = code.trim().split('-').slice(1).join('-');
  if (isSalesTaxJurisdiction(code) && state) {
    try {
      const regionNames = new Intl.DisplayNames([locale ?? 'en'], { type: 'region' });
      return `${state}, ${regionNames.of('US') ?? 'USA'}`;
    } catch {
      return code;
    }
  }
  try {
    const regionNames = new Intl.DisplayNames([locale ?? 'en'], { type: 'region' });
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Convenience function that returns a display label for a jurisdiction code.
 * Combines the Intl-derived country name with the localized tax abbreviation.
 * e.g. "Netherlands (BTW)", "France (TVA)", "ID, United States (Sales Tax)"
 */
export function getJurisdictionLabel(code: string, locale?: string): string {
  const name = getJurisdictionName(code, locale);
  const taxName = resolveTaxName(code);
  return `${name} (${taxName})`;
}

/** Stable empty list for `rateRows` while the rates load. */
const EMPTY_RATE_ROWS: readonly TaxRateWithLineage[] = [];

export interface JurisdictionTaxConfigOptions {
  /**
   * `tax_rate_id`s the current document's lines already point at. A rate in
   * this set is listed even when it has since been retired (`is_active =
   * false`), flagged `isInactive`, so an existing bill or expense keeps
   * showing the rate it was posted with instead of a blank picker.
   */
  referencedRateIds?: readonly string[];
  /**
   * The document's date (`YYYY-MM-DD`; a `YYYY-MM-DDTHH:mm` form value is
   * read as its day). Only rates whose window covers that day may become the
   * default or match a percentage — an org rate is never deactivated when it
   * expires or before it starts (a removed town, a future quarter), it carries
   * `effective_from` / `effective_to` instead. The other active rates of the
   * direction are still offered, after the in-window ones, flagged
   * `otherDate` (a deliberate pick for an earlier or later supply). Omitted →
   * today, for every direction-filtered (picker) caller; direction-less
   * callers (the tax pages) keep the full active list.
   */
  asOfDate?: string | null;
  /**
   * A line of the current document is stored as `exempt` (`tax_rate =
   * 'exempt'`, `tax_treatment = 'exempt'`). A VAT purchase picker offers no
   * "Exempt" (a purchase exemption is the supplier's matter); with this set it
   * lists "Exempt" anyway, flagged `isInactive` — so a legacy exempt purchase
   * line still shows, and saves, the value it was posted with, while no other
   * line can pick it. No effect on OUTPUT or direction-less callers, which
   * always list "Exempt".
   */
  hasLegacyExempt?: boolean;
}

/** The calendar day of a date-ish form value, or null. */
export function dayOf(value: string | null | undefined): string | null {
  return /^\d{4}-\d{2}-\d{2}/.exec(value ?? '')?.[0] ?? null;
}

/**
 * The VAT-vs-sales-tax decision for a jurisdiction code
 * (`isSalesTaxJurisdiction`: a US code — `US-VT`, or a bare `US` country — is
 * sales tax, everything else and no jurisdiction yet is VAT). The one source
 * the line forms and the AR Issue pre-check read.
 */
export function taxSystemOf(jurisdiction: string | null | undefined): 'VAT' | 'SALES_TAX' {
  return isSalesTaxJurisdiction(jurisdiction) ? 'SALES_TAX' : 'VAT';
}

/**
 * The selected org's tax jurisdiction: the saved `primary_tax_jurisdiction`
 * first (authoritative), else the org's country, blanks unset and trimmed
 * (`resolveTaxJurisdictionFrom`, the twin of the SQL's `_org_tax_jurisdiction`). `isLoading` is the settings
 * read — until it lands the country fallback may name the wrong jurisdiction
 * (a saved `US-VT` or a different country than the org's), so a caller that
 * blocks on the tax system waits for it.
 */
export function useOrgTaxJurisdiction() {
  const { selectedOrganization } = useOrganization();
  const { data: taxSettings, isLoading } = useFullTaxSettings(selectedOrganization?.id);
  const jurisdiction = resolveTaxJurisdictionFrom(
    taxSettings?.primary_tax_jurisdiction,
    selectedOrganization?.country,
  );
  return { jurisdiction, taxSettings, isLoading };
}

/**
 * Hook to get tax configuration based on organization's jurisdiction.
 * All tax rate data comes from the database (tax_rates table).
 * Tax system and name are derived from DB tax_type and jurisdiction code.
 * Exemption reasons and UI labels still come from i18n.
 *
 * @param direction - Filter rates by direction: 'INPUT' for AP/expenses, 'OUTPUT' for AR
 * @param options - `referencedRateIds`: retired rates to keep visible (see above)
 */
export function useJurisdictionTaxConfig(
  direction?: TaxDirection,
  options?: JurisdictionTaxConfigOptions,
): JurisdictionTaxConfig {
  const { selectedOrganization } = useOrganization();
  const { t, i18n } = useTranslation();

  const organizationId = selectedOrganization?.id;

  // Normalise the referenced-id set to a stable string so a caller passing a
  // fresh array each render doesn't defeat the memo below.
  const referencedRateKey = Array.from(new Set(options?.referencedRateIds ?? []))
    .filter(Boolean)
    .sort()
    .join('|');
  const hasLegacyExempt = options?.hasLegacyExempt === true;

  // Saved primary_tax_jurisdiction first, else the org country.
  const { jurisdiction, taxSettings } = useOrgTaxJurisdiction();

  // The VAT filing start date rides on the settings row this hook already
  // loads, so every tax surface reaches it without a second query.
  const filingStartDate = taxSettings?.vat_filing_start_date ?? null;

  // Fetch real tax rates from the database
  const { data: dbTaxRates, isLoading: taxRatesLoading } = useOrganizationTaxRates(organizationId);

  // The day whose rates the picker offers (see `asOfDate`). Pickers default to
  // today; the tax pages (no direction) list every active rate.
  const asOfDay = dayOf(options?.asOfDate) ?? (direction ? formatLocalISODate(new Date()) : null);

  return useMemo(() => {
    // No jurisdiction set - require it
    if (!jurisdiction) {
      return {
        jurisdiction: null,
        jurisdictionName: '',
        taxSystem: 'VAT',
        taxName: 'VAT',
        rates: [],
        defaultRate: 'standard',
        exemptionReasons: [],
        exemptionRegion: 'eu',
        taxColumnLabel: t('tax.ui.taxColumn.vat'),
        totalTaxLabel: t('tax.ui.taxBreakdown.totalTax.vat'),
        filingLagDays: DEFAULT_FILING_LAG_DAYS,
        filingRounding: DEFAULT_FILING_ROUNDING,
        filingStartDate,
        isConfigured: false,
        error: 'country_required',
        asOfDay,
        rateRows: dbTaxRates ?? EMPTY_RATE_ROWS,
      };
    }

    // Check if jurisdiction is in EU
    const isEU = EU_VAT_COUNTRIES.includes(jurisdiction as EUCountry);

    // Build rates from DB tax_rates, filtered by direction and active status
    const matchesDirection = (row: { direction: string }) => {
      if (direction) return row.direction === direction;
      // If no direction specified, show INPUT and OUTPUT (exclude INTERNAL)
      return row.direction === 'INPUT' || row.direction === 'OUTPUT';
    };
    // Window first, default second: a rate that has expired (or not started)
    // on the document's day can be neither offered nor the default.
    const inWindow = (row: { effective_from?: string | null; effective_to?: string | null }) =>
      asOfDay === null || isRateEffectiveOn(row, asOfDay);
    const filteredDbRates = (dbTaxRates ?? []).filter(
      row => row.is_active && matchesDirection(row) && inWindow(row),
    );
    // Active rates of the direction whose window does NOT cover the day — a
    // rate a rate change retired, or one that has not started. Offered after
    // everything else (see `otherDate`), never a default or a match.
    const otherDateDbRates = asOfDay === null
      ? []
      : (dbTaxRates ?? [])
        .filter(row => row.is_active && matchesDirection(row) && !inWindow(row))
        .sort((a, b) => a.name.localeCompare(b.name) || (a.effective_from ?? '').localeCompare(b.effective_from ?? ''));
    const listedIds = new Set([...filteredDbRates, ...otherDateDbRates].map(row => row.id));

    const rates: TaxRate[] = filteredDbRates.map(row => ({
      key: row.id, // UUID — used as select value
      value: row.rate / 100, // decimal for calculation compatibility (21 → 0.21)
      label: row.name, // e.g. "BTW 21%"
      numericValue: row.rate, // percentage (21, 9, 0)
      requiresExemptionReason: row.rate === 0,
      fromDb: true,
    }));

    // Retired (`is_active = false`) rates the lines still point at. They sit
    // after the active rates and are flagged so the pickers render them
    // disabled and the default resolution below skips them — the line keeps
    // its stored rate on screen, but nothing new is posted against a retired
    // rate.
    const referencedIds = new Set(referencedRateKey ? referencedRateKey.split('|') : []);
    (dbTaxRates ?? [])
      .filter(row => !listedIds.has(row.id) && referencedIds.has(row.id) && matchesDirection(row))
      .forEach(row => {
        rates.push({
          key: row.id,
          value: row.rate / 100,
          label: t('tax.ui.inactiveRate', '{{name}} (inactive)', { name: row.name }),
          numericValue: row.rate,
          requiresExemptionReason: row.rate === 0,
          isInactive: true,
          fromDb: true,
        });
      });

    // Derive tax system from jurisdiction code (mirrors taxName below).
    // Direction-filtered rates can be empty (e.g. US orgs with no reclaimable-INPUT
    // rates), so we must not depend on them for the system classification.
    const taxSystem = taxSystemOf(jurisdiction);

    // Derive tax name from jurisdiction code, matching getJurisdictionLabel logic
    const taxName = resolveTaxName(jurisdiction);

    // Synthetic zero-tax options (not DB rates — they're tax treatments).
    //
    // VAT: "Exempt" is a sales-side (OUTPUT) choice — and on the direction-less
    // tax pages — that must state why (exemption reason). Not on purchases
    // (INPUT): a purchase exemption is the supplier's matter, and on a purchase
    // it posts exactly like the 0% option. A legacy purchase line stored as
    // exempt keeps it, listed disabled (`hasLegacyExempt`). Before it, when the
    // org has no active DB rate at 0% in this direction, a plain "No VAT (0%)" option — a 0% line that
    // needs no reason (it saves as zero_rated with no tax_rate_id, exactly like
    // a plain 0). An org with a DB 0% rate uses that row instead, so the picker
    // never lists two 0% options.
    //
    // US sales tax: zero-tax splits into two legally distinct cases, both posting
    // $0 but meaning different things — so we offer them separately.
    //   • "No tax" — on the sales side: a non-taxable item, or no nexus in the
    //     buyer's state (the buyer may owe use tax). On the purchase side: the
    //     vendor charged no sales tax, which is most US purchase lines. No
    //     certificate, so no exemption reason required. It is offered on BOTH
    //     directions so a purchase picker is never blank — a US org typically
    //     has no INPUT rates at all, and without an explicit 0 option the AP
    //     bill picker rendered empty even though 0 was the value on every line.
    //   • "Exempt (certificate)" — an otherwise-taxable sale made tax-free by the
    //     buyer's status/use (resale/nonprofit/government). Backed by a certificate,
    //     so it forces an exemption reason. Sales (OUTPUT) side only — a purchase
    //     exemption is the vendor's problem, not something we post.
    // Direction-less callers (tax pages) keep the bare DB list.
    if (taxSystem === 'SALES_TAX') {
      if (direction) {
        rates.push({
          key: 'no_tax',
          value: 0,
          label: t('tax.ui.noTax', 'No tax'),
          numericValue: 0,
          requiresExemptionReason: false,
        });
      }
      if (direction === 'OUTPUT') {
        rates.push({
          key: 'exempt',
          value: -1,
          label: t('tax.ui.exemptCertificate', 'Exempt (certificate)'),
          numericValue: 0,
          requiresExemptionReason: true,
        });
      }
    } else {
      if (direction && !filteredDbRates.some(row => row.rate === 0)) {
        rates.push({
          key: 'no_vat',
          value: 0,
          // On a purchase it is simply "No VAT" — the supplier charged none.
          // "Zero-rated" is a property of a supply, not of a purchase line.
          label: direction === 'INPUT'
            ? t('tax.ui.noVatPurchase', 'No VAT')
            : t('tax.ui.noVat', 'No {{taxName}} (0%)', { taxName }),
          numericValue: 0,
          requiresExemptionReason: false,
        });
      }
      // "VAT not reclaimable" — purchases only, beside the 0% option (the
      // synthetic "No VAT" or the org's own DB 0% rate): VAT WAS charged, but
      // the org cannot deduct it on its VAT return (foreign VAT it will not
      // claim back, restaurant costs, private use, purchases for exempt
      // activities). Stored as tax_treatment 'non_reclaimable' at rate 0 with
      // no tax_rate_id, so it posts exactly like 0% — the gross amount to
      // cost, no VAT leg — but stays distinguishable from "no VAT was
      // charged". Never a default, never the scan-miss 0% option, no
      // exemption reason (it is not an exemption). docs/TAX.md.
      if (direction === 'INPUT') {
        rates.push({
          key: 'non_reclaimable',
          value: 0,
          label: t('tax.ui.vatNotReclaimable', 'VAT not reclaimable'),
          numericValue: 0,
          requiresExemptionReason: false,
        });
      }
      if (direction !== 'INPUT' || hasLegacyExempt) {
        rates.push({
          key: 'exempt',
          value: -1,
          label: t('tax.ui.exempt', 'Exempt'),
          numericValue: 0,
          requiresExemptionReason: true,
          ...(direction === 'INPUT' && { isInactive: true }),
        });
      }
    }

    // The other-date rates go last, after the synthetics: the picker renders
    // them under their own heading.
    otherDateDbRates.forEach(row => {
      rates.push({
        key: row.id,
        value: row.rate / 100,
        label: row.name,
        numericValue: row.rate,
        requiresExemptionReason: row.rate === 0,
        fromDb: true,
        otherDate: { from: row.effective_from ?? null, to: row.effective_to ?? null },
      });
    });

    // Find default rate key from DB (is_default = true). Only an active rate
    // (or a synthetic) may be the default — a referenced retired rate never is.
    // "Exempt" is never a default either: it is always a deliberate user choice,
    // because it must say why the line is exempt. Nor is "VAT not reclaimable"
    // — a deliberate statement about one purchase, never an assumption.
    // Read on the picker's day — or today on the direction-less tax pages,
    // whose list is not window-filtered: a rate change leaves two is_default
    // rows per direction with non-overlapping windows (20261029100000).
    const defaultDbRate = findEffectiveDefault(filteredDbRates, asOfDay ?? formatLocalISODate(new Date()) ?? '');
    const defaultRate = defaultDbRate?.id
      ?? rates.find(r => !r.isInactive && !r.otherDate && r.key !== 'exempt' && r.key !== 'non_reclaimable')?.key
      ?? 'standard';

    // Determine exemption region (EU uses EU exemptions, non-EU uses US-style exemptions)
    const exemptionRegion = isEU ? 'eu' : 'us';

    // Get exemption reasons for the region (still from i18n — legal/regulatory text)
    const exemptionData = t(`tax.exemptionReasons.${exemptionRegion}`, { returnObjects: true }) as Record<string, unknown>;

    const exemptionReasons: ExemptionReason[] = [];
    if (exemptionData && typeof exemptionData === 'object') {
      Object.entries(exemptionData).forEach(([key, value]) => {
        if (key === 'placeholder') return; // Skip placeholder

        if (typeof value === 'object' && value !== null) {
          const reasonData = value as Record<string, unknown>;
          exemptionReasons.push({
            key,
            label: String(reasonData.label || key),
            invoiceText: reasonData.invoiceText ? String(reasonData.invoiceText) : undefined,
            placeholder: reasonData.placeholder ? String(reasonData.placeholder) : undefined,
            requiresCertificate: Boolean(reasonData.requiresCertificate),
          });
        }
      });
    }

    // Jurisdiction display name via Intl API
    const jurisdictionName = getJurisdictionName(jurisdiction, i18n.language);

    // UI labels based on tax system
    const taxColumnLabel = taxSystem === 'SALES_TAX'
      ? t('tax.ui.taxColumn.salesTax')
      : t('tax.ui.taxColumn.vat');

    const totalTaxLabel = taxSystem === 'SALES_TAX'
      ? t('tax.ui.taxBreakdown.totalTax.salesTax')
      : t('tax.ui.taxBreakdown.totalTax.vat');

    return {
      jurisdiction,
      jurisdictionName,
      taxSystem,
      taxName,
      rates,
      defaultRate,
      exemptionReasons,
      exemptionRegion,
      taxColumnLabel,
      totalTaxLabel,
      filingLagDays: getFilingLagDays(jurisdiction),
      filingRounding: getFilingRoundingPolicy(jurisdiction),
      filingStartDate,
      isConfigured: !taxRatesLoading,
      error: null,
      asOfDay,
      rateRows: dbTaxRates ?? EMPTY_RATE_ROWS,
    };
  }, [jurisdiction, filingStartDate, t, i18n.language, dbTaxRates, taxRatesLoading, direction, referencedRateKey, asOfDay, hasLegacyExempt]);
}

/**
 * Resolve the org's default INPUT (purchase/expense) tax rate as a concrete
 * `{ rate, rateId }` pair — the percentage plus the DB rate UUID — or
 * `undefined` when there's nothing to apply: a SALES_TAX (US) org, a synthetic
 * (non-DB) default, or the tax-rates query not yet loaded.
 *
 * Shared by the bank-feed expense flows (the headless one-click quick-add and
 * the full Create-Expense dialog) so an imported line seeds the exact same
 * default the invoice form's default-rate fill applies to a placeholder line —
 * the same rate identity (`taxRateIdOf`: a DB rate, never a synthetic
 * treatment) that `InvoiceLineItems`' fill effect uses.
 */
export function resolveDefaultInputTaxRate(
  taxConfig: JurisdictionTaxConfig,
): { rate: number; rateId: string } | undefined {
  if (taxConfig.taxSystem === 'SALES_TAX') return undefined;
  const match = taxConfig.rates.find((r) => r.key === taxConfig.defaultRate);
  const rateId = taxRateIdOf(match);
  return match && rateId ? { rate: match.numericValue, rateId } : undefined;
}

/**
 * The ONE selectable DB rate at `percentage` in an already direction- and
 * date-filtered list, or undefined when there is none or more than one (two
 * rates may share a percentage — Vermont meals 9% / rooms 9%, an NL org's
 * "BTW 21%" and "BTW 21% events" — and a line never guesses between them).
 * Retired (`isInactive`), other-date (`otherDate`) and synthetic 0% options
 * never count.
 *
 * `tolerance` (percentage points) is for a rate COMPUTED from a document's
 * totals (tax ÷ subtotal, rounding noise): 0.01 lets 20.995 match 21 when 21
 * is the only rate that close. A printed rate is matched exactly (0).
 */
export function uniqueRateAtPercentage(
  rates: readonly TaxRate[],
  percentage: number,
  tolerance = 0,
): TaxRate | undefined {
  // Float slack only — 21 vs 21.000000001 from a division is still "exact".
  const slack = tolerance + 1e-9;
  const matches = rates.filter(
    (r) => r.fromDb && !r.isInactive && !r.otherDate && Math.abs(r.numericValue - percentage) <= slack,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

/** The tolerance a computed (tax ÷ subtotal) scan hint matches within. */
export const COMPUTED_RATE_HINT_TOLERANCE = 0.01;

/**
 * Helper to convert tax rate value to form select value.
 * Now supports UUID keys (from DB) and legacy string keys.
 *
 * @param taxRateId - the line's stored `tax_rate_id`, when known. An exact id
 *   match wins over the numeric match, so a line posted against a specific
 *   rate (including a retired one listed via `referencedRateIds`) displays
 *   that rate and not merely the first rate at the same percentage.
 */
export function taxRateToSelectValue(
  taxRate: number | string | undefined | null,
  rates: TaxRate[],
  taxRateId?: string | null,
): string {
  if (taxRate === undefined || taxRate === null) {
    // Return first rate as default
    return rates[0]?.key || 'standard';
  }

  if (taxRate === 'exempt' || taxRate === -1) return 'exempt';

  // Before the id match: the treatment, not a (stale) rate id, says what the
  // line is — a "VAT not reclaimable" line never names a rate.
  if (taxRate === 'non_reclaimable') return 'non_reclaimable';

  if (taxRateId && rates.some(r => r.key === taxRateId)) return taxRateId;

  // Find matching rate by numeric value (percentage, e.g. 21)
  if (typeof taxRate === 'number') {
    // A taxed line without an id shows a rate only when exactly one rate has
    // its percentage — on a tie the picker stays blank (the user picks; the
    // api refuses an id-less taxed line). A 0 keeps the first-match lookup so
    // a stored 0% line still shows the 0% / exempt option.
    const matchingRate = taxRate > 0
      ? uniqueRateAtPercentage(rates, taxRate)
      : rates.find(r => r.numericValue === taxRate);
    if (matchingRate) return matchingRate.key;
    // No rate at this percentage — leave the picker blank rather than show the
    // first option's label for a value it does not represent.
    return '';
  }

  // Return as-is if it's already a key (UUID or legacy key)
  if (typeof taxRate === 'string') {
    const matchingRate = rates.find(r => r.key === taxRate);
    if (matchingRate) return matchingRate.key;
  }

  // Default to first rate
  return rates[0]?.key || 'standard';
}

/**
 * Helper to convert select value back to tax rate for storage.
 * Returns the numeric percentage value (e.g. 21), 'exempt' or
 * 'non_reclaimable'.
 */
export function selectValueToTaxRate(
  selectValue: string,
  rates: TaxRate[]
): TaxRateType {
  const rate = rates.find(r => r.key === selectValue);

  if (!rate) {
    return 0;
  }

  if (rate.value === -1 || rate.key === 'exempt') {
    return 'exempt';
  }

  if (rate.key === 'non_reclaimable') {
    return 'non_reclaimable';
  }

  // Return numeric percentage value
  return rate.numericValue;
}

/**
 * Helper to get the tax_rate_id (UUID) for a given select key.
 * Returns undefined for synthetic rates like 'exempt' / 'no_vat' / 'no_tax'.
 */
export function selectValueToTaxRateId(
  selectValue: string,
  rates: TaxRate[]
): string | undefined {
  return taxRateIdOf(rates.find(r => r.key === selectValue));
}
